// How a booking chip is PAINTED — the one decision, shared by the renderer and the legend.
//
// ⚠ WHY THIS IS A MODULE AND NOT JUST INLINE IN DayView. The board legend used to be a hardcoded
// array of eight swatches, written when the fill carried the booking's STATUS. The 0.12.3 chip
// work moved status to the left bar, the booking type to a new top bar and the instructor to the
// fill — and never touched the legend. So the legend went on explaining a scheme the board had
// stopped using: it showed "Confirmed" as a green fill (the fill is now the instructor), "Pending
// approval" as an amber fill (only the dashed border was still true), and an amber dot for
// "Student not yet notified" that RENDERED NOWHERE — at the time, `student_notified` produced the
// words "✓ notified" on row one, and there was never a dot. (⚠ 2026-09-17: that word is gone as
// well. The chip makes no notification claim at all now; it carries only the amber phone WARNING,
// decided by lib/notifyWarning.ts.)
//
// A legend that approximates the chip drifts from the chip. This function is what both of them
// call, so a change to the paint changes the swatch in the same commit or fails the build.
//
// ⚠ THE PALETTE IS A PARAMETER, not an import. This module lives under lib/ and the theme lives
// under components/; taking the colours in keeps the dependency pointing one way and lets the
// verification gate drive it without pulling in a component tree.

/** The theme values a chip's paint depends on. A subset of ops-board `C`. */
export interface ChipPalette {
  pill: string;
  red: string;
  green: string;
  amber: string;
  textMuted: string;
}

/**
 * The mutually exclusive chip states, in the SAME priority order the renderer applies them.
 * ⚠ Order is load-bearing: a cancelled provisional booking paints as provisional, because that
 * is what the if/else chain in bookingRect does.
 */
export type ChipState =
  | 'provisional'   // queued on this device, not yet replayed (5d-p2)
  | 'conflict'      // replayed and rejected — needs attention (5d-p2)
  | 'cancelled'
  | 'unavailable'   // an is_unavailability kind — a muted resource blocker (Feature B)
  | 'maintenance'   // firm maintenance — SOLID kind colour (Ben 2026-06-19)
  | 'ordinary';

export interface ChipVisualInput {
  state: ChipState;
  /** The resolved FILL-channel colour (the instructor by default), or null when there is none. */
  fillColor: string | null;
  /** The booking kind's colour — only used by the solid maintenance fill. */
  kindColor: string | null;
  /** Slate, for the unavailable state. */
  unavailColor: string;
  /** Hex alpha suffix for the ordinary fill (FILL_ALPHA, '30' = 19 percent). */
  fillAlpha: string;
  pending: boolean;
  declined: boolean;
  outsideHours: boolean;
  confirmed: boolean;
  C: ChipPalette;
}

export interface ChipVisual {
  bg: string;
  border: string;
  /** The 4px LEFT bar — confirmation status. null on every special state, which is deliberate. */
  statusStripe: string | null;
  strokeDasharray: string;
  strokeWidth: number;
  opacity: number;
}

/**
 * ⚠ THE SPECIAL STATES KEEP THEIR OWN FILL AND GET NO STATUS STRIPE, and that has a consequence
 * worth stating rather than discovering: ON THOSE CHIPS THE INSTRUCTOR COLOUR IS NOT SHOWN. That
 * is the right priority — those chips are saying something more urgent than who is flying — and
 * it is the same reasoning that already suppresses the stripe. The TOP BAR still renders on them,
 * because the booking type is true regardless of status.
 */
export function chipVisual(inp: ChipVisualInput): ChipVisual {
  const { state, C } = inp;
  let bg: string;
  let border: string;
  let statusStripe: string | null = null;

  if (state === 'provisional') { bg = '#f5f3ff'; border = '#7c3aed'; }
  else if (state === 'conflict') { bg = '#fef2f2'; border = C.red; }
  else if (state === 'cancelled') { bg = '#f1f5f9'; border = C.red; }
  else if (state === 'unavailable') { bg = `${inp.unavailColor}22`; border = inp.unavailColor; }
  else if (state === 'maintenance' && inp.kindColor) { bg = inp.kindColor; border = inp.kindColor; }
  else {
    // ⚠ THE FALLBACK IS THE NEUTRAL C.pill, NOT THE KIND COLOUR. A booking with no instructor, or
    // an external one with no user record, must READ as uncoloured. Falling back to the type would
    // make an uncoloured chip look like a coloured one — the fill would be lying about who is
    // flying.
    bg = inp.fillColor ? `${inp.fillColor}${inp.fillAlpha}` : C.pill;
    border = inp.fillColor ?? C.textMuted;
    statusStripe = (inp.pending || inp.outsideHours) ? C.amber
      : inp.declined ? C.red
      : inp.confirmed ? C.green
      : C.textMuted;
  }

  const maint = state === 'maintenance' && !!inp.kindColor;
  return {
    bg,
    border,
    statusStripe,
    strokeDasharray: state === 'provisional' || state === 'conflict' ? '3,3'
      : state === 'cancelled' ? '4,2'
      : inp.pending ? '5,3'
      : 'none',
    strokeWidth: state === 'provisional' || state === 'conflict' || inp.pending || maint ? 1.5 : 1,
    opacity: state === 'cancelled' || inp.declined ? 0.55 : 1,
  };
}
